/**
 * /admin/users/new — form for admin pre-provisioning.
 *
 * Search-as-you-type box at the top finds candidates in the tenant's
 * Entra directory (Microsoft Graph `User.ReadBasic.All`, backed by the
 * `directory-search` admin endpoint). Picking a candidate auto-populates
 * the entra_object_id, email, and display_name fields. The admin
 * confirms the roles + reason and submits.
 *
 * Fallback for the case where the Graph permission isn't yet
 * admin-consented (503 `graph_permission_denied`) or Graph is otherwise
 * unavailable (503 `graph_error`): a "Type Entra object ID manually"
 * toggle reveals the legacy text input so the form is never blocked.
 *
 * Validation rule: either a candidate must be selected OR the manual
 * fields must be filled. The submit POSTs `ProvisionUserIn` exactly as
 * before — backend contract unchanged.
 */

import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Anchor,
  Button,
  Collapse,
  Group,
  Loader,
  Paper,
  Radio,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconArrowLeft, IconSearch } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { ApiError } from "@/shared/api/apiFetch";
import { type EntraUserSearchHit, provisionUser, searchDirectory } from "@/shared/api/users";
import { toastSuccess } from "@/shared/ui/toast";

const AVAILABLE_ROLES = ["user", "developer", "administrator"];
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_MIN_CHARS = 2;

interface FormValues {
  entra_object_id: string;
  email: string;
  display_name: string;
  // A single role — roles are hierarchical (administrator ⊇ developer ⊇ user),
  // so exactly one is assigned and the policy layer grants the lower tiers.
  role: string;
  reason: string;
}

export function ProvisionUser() {
  const getToken = useAccessToken();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Directory search state.
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<EntraUserSearchHit[]>([]);
  const [graphUnavailable, setGraphUnavailable] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const form = useForm<FormValues>({
    initialValues: {
      entra_object_id: "",
      email: "",
      display_name: "",
      role: "user",
      reason: "",
    },
    validate: {
      entra_object_id: (v) =>
        v.trim().length === 0
          ? "Pick a user from the directory or enter the Entra object ID manually"
          : null,
      email: (v) => (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : "Looks like a bad email"),
      display_name: (v) => (v.trim().length === 0 ? "Required" : null),
      reason: (v) => (v.trim().length < 3 ? "Audit reason ≥ 3 characters" : null),
    },
  });

  // Debounced directory search. Fires on `query` change (>= MIN_CHARS).
  useEffect(() => {
    if (debounceRef.current !== null) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (query.trim().length < SEARCH_MIN_CHARS) {
      setResults([]);
      setSearching(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const token = await getToken();
        const { items } = await searchDirectory(token, query.trim());
        setResults(items);
        setGraphUnavailable(false);
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.code === "graph_permission_denied" || err.code === "graph_error")
        ) {
          // Soft fail — let the admin proceed with manual entry.
          setGraphUnavailable(true);
          setManualMode(true);
          setResults([]);
        } else {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, getToken]);

  const handlePick = (hit: EntraUserSearchHit) => {
    form.setValues({
      entra_object_id: hit.entra_object_id,
      email: hit.email ?? hit.user_principal_name,
      display_name: hit.display_name,
    });
    setQuery(hit.display_name);
    setResults([]);
  };

  const onSubmit = async (values: FormValues) => {
    setError(null);
    setBusy(true);
    try {
      const token = await getToken();
      const { role, ...rest } = values;
      const created = await provisionUser(token, { ...rest, roles: [role] });
      toastSuccess("User provisioned", `${values.display_name} can now sign in.`);
      navigate(`/admin/users/${created.user_id}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === "user_already_exists") {
        setError("A user with that Entra object ID already exists in this tenant.");
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const selectedSummary = form.values.entra_object_id ? (
    <Paper withBorder radius="sm" p="sm" bg="brand.0">
      <Stack gap={2}>
        <Text size="sm" fw={600}>
          Selected: {form.values.display_name || "(no name)"}
        </Text>
        <Text size="xs" c="dimmed">
          {form.values.email}
        </Text>
        <Text size="xs" c="dimmed">
          oid: {form.values.entra_object_id}
        </Text>
      </Stack>
    </Paper>
  ) : null;

  return (
    <Stack gap="md">
      <Group>
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate("/admin/users")}
        >
          Back to users
        </Button>
      </Group>

      <Title order={2}>Provision user</Title>

      <Paper withBorder radius="md" p="lg" maw={520}>
        <form onSubmit={form.onSubmit(onSubmit)}>
          <Stack gap="md">
            {!manualMode && (
              <>
                <TextInput
                  label="Find user in directory"
                  description="Type at least 2 characters of a name, email, or UPN. Results come from your Entra tenant."
                  placeholder="e.g. Alice or alice@daquma.com"
                  leftSection={<IconSearch size={16} />}
                  rightSection={searching ? <Loader size="xs" /> : null}
                  value={query}
                  onChange={(event) => setQuery(event.currentTarget.value)}
                  autoFocus
                />
                {results.length > 0 && (
                  <Paper withBorder radius="sm" style={{ maxHeight: 240, overflowY: "auto" }}>
                    <Stack gap={0}>
                      {results.map((hit) => (
                        <Button
                          key={hit.entra_object_id}
                          variant="subtle"
                          justify="flex-start"
                          color="gray"
                          onClick={() => handlePick(hit)}
                          styles={{
                            root: { height: "auto", padding: "8px 12px" },
                            label: { width: "100%" },
                          }}
                        >
                          <Stack gap={0} align="flex-start" style={{ width: "100%" }}>
                            <Text size="sm" fw={500}>
                              {hit.display_name}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {hit.email ?? hit.user_principal_name}
                            </Text>
                          </Stack>
                        </Button>
                      ))}
                    </Stack>
                  </Paper>
                )}
                {selectedSummary}
                {graphUnavailable && (
                  <Alert variant="light" color="yellow" title="Directory search unavailable">
                    Microsoft Graph search isn't reachable right now (the permission may not be
                    admin-consented yet). Type the Entra object ID manually instead.
                  </Alert>
                )}
                <Anchor
                  component="button"
                  type="button"
                  size="sm"
                  onClick={() => setManualMode(true)}
                >
                  Type Entra object ID manually
                </Anchor>
              </>
            )}

            <Collapse in={manualMode}>
              <Stack gap="md">
                <TextInput
                  label="Entra object ID"
                  description="Found via Az CLI: az ad user show --id colleague@daquma.com --query id -o tsv"
                  placeholder="e.g. 4a3b..."
                  required
                  {...form.getInputProps("entra_object_id")}
                />
                <Anchor
                  component="button"
                  type="button"
                  size="sm"
                  onClick={() => {
                    setManualMode(false);
                    form.setFieldValue("entra_object_id", "");
                  }}
                >
                  Search the directory instead
                </Anchor>
              </Stack>
            </Collapse>

            <TextInput label="Email" type="email" required {...form.getInputProps("email")} />
            <TextInput label="Display name" required {...form.getInputProps("display_name")} />
            <Radio.Group
              label="Role"
              description="One role per user. Higher roles include the lower ones — Administrator can do everything a Developer and User can; Developer includes User."
              {...form.getInputProps("role")}
            >
              <Group mt="xs">
                {AVAILABLE_ROLES.map((r) => (
                  <Radio key={r} value={r} label={r} />
                ))}
              </Group>
            </Radio.Group>
            <Textarea
              label="Audit reason"
              description="Recorded against this action in the audit log (≥ 3 characters)."
              autosize
              minRows={2}
              required
              {...form.getInputProps("reason")}
            />
            {error && (
              <Alert variant="light" color="red" title="Could not provision">
                {error}
              </Alert>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => navigate("/admin/users")} disabled={busy}>
                Cancel
              </Button>
              <Button type="submit" loading={busy}>
                Provision
              </Button>
            </Group>
          </Stack>
        </form>
      </Paper>
    </Stack>
  );
}
